'use client';

import React, { ComponentProps, PropsWithChildren } from 'react';
import cx from 'classnames';
import { LogEventArgs, logEvent } from '../lib/analytics';

interface ButtonProps<Tag extends React.ElementType<any>> {
  is?: Tag;
  size?: 'LARGE' | 'EXTRA_LARGE';
  variant: 'PRIMARY' | 'SECONDARY' | 'CTA';
  isFullWidth?: boolean;
  className?: string;
  analytics?: LogEventArgs;
}

export const Button = <Tag extends React.ElementType>({
  is,
  size,
  variant,
  isFullWidth,
  className,
  children,
  analytics,
  onClick,
  ...props
}: PropsWithChildren<ButtonProps<Tag>> &
  Omit<ComponentProps<Tag>, keyof ButtonProps<Tag>>) => {
  const Component = is ?? 'button';

  return (
    <Component
      {...props}
      className={cx(
        'cursor-pointer disabled:pointer-events-none disabled:opacity-50 border border-transparent text-base block font-semibold py-2 px-4 rounded leading-tight focus:outline-none focus:shadow-outline text-center whitespace-no-wrap',
        {
          truncate: Component === 'button',
          'py-3': size === 'LARGE',
          'text-xl py-3': size === 'EXTRA_LARGE',
          'bg-blue-500 hover:bg-blue-700 text-white': variant === 'PRIMARY',
          'bg-bright-purple hover:bg-indigo-500 text-white': variant === 'CTA',
          'bg-gray-200 hover:bg-gray-300 text-gray-700':
            variant === 'SECONDARY',
          'w-full': isFullWidth,
        },
        className,
      )}
      onClick={() => {
        analytics && logEvent(analytics);
        onClick?.();
      }}
    >
      {children}
    </Component>
  );
};
Button.displayName = 'Button';
